import { useCallback, useMemo, useState } from 'react';
import { PrimaryTableRow } from '../styledComponents';
import { ExtendedWarrantyLineConfig, ExtendedWarrantyOutput } from '../../types';
import { getDurationLabel } from '../../handlers';

type Props = {
  configs: ExtendedWarrantyLineConfig[];
  configState: ExtendedWarrantyOutput;
  readOnly: boolean;
  onChange: (newValue: ExtendedWarrantyOutput) => void;
};

export const ExtendedWarrantySelectAll = ({ configs, configState, readOnly, onChange }: Props) => {
  const durationOptions: number[] = useMemo(() => {
    return configs.reduce((options, config) => {
      config.warrantyOptions.forEach((option) => {
        if (!options.includes(option.duration)) {
          options.push(option.duration);
        }
      });

      return options;
    }, [] as number[]);
  }, [configs]);

  const [selectedDuration, setDuration] = useState(() => {
    return configs.reduce((maxDuration, config) => {
      return config.warrantyMaxExtDuration > maxDuration
        ? config.warrantyMaxExtDuration
        : maxDuration;
    }, 0);
  });

  const handleDurationSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedDuration = Number(event.target.value);

      const updatedState = Object.keys(configState).reduce((updatedState, warrantedPartNumber) => {
        const configInformation = configs.find(
          (config) => config.warrantedPartNumber === warrantedPartNumber
        )!;

        const targetDuration =
          selectedDuration > configInformation.warrantyMaxExtDuration
            ? configInformation.warrantyMaxExtDuration
            : selectedDuration;

        updatedState[warrantedPartNumber] = {
          ...configState[warrantedPartNumber],
          warrantyDuration: targetDuration,
        };
        return updatedState;
      }, {} as ExtendedWarrantyOutput);

      setDuration(selectedDuration);
      onChange(updatedState);
    },
    [configs, configState, onChange]
  );

  return (
    <PrimaryTableRow>
      <td colSpan={4} />
      <td colSpan={1}>
        <label htmlFor="select-all">All Extended Warranty Durations</label>
      </td>
      <td colSpan={2}>
        <select
          id="select-all"
          onChange={handleDurationSelect}
          value={selectedDuration}
          disabled={readOnly}
        >
          {durationOptions.map((duration) => (
            <option key={duration} value={duration}>
              {getDurationLabel(duration)}
            </option>
          ))}
        </select>
      </td>
    </PrimaryTableRow>
  );
};
